import { GeoJSON } from 'leaflet';

export enum MarkupTypes {
  Point = 'Point',
  Line = 'Line',
  Polygon = 'Polygon',
  Rectangle = 'Rectangle',
  Text = 'Text',
  Callout = 'Callout',
}

export const markupTypesList = [
  {
    id: MarkupTypes.Point,
    label: 'Point',
    icon: 'location_on',
  },
  {
    id: MarkupTypes.Line,
    label: 'Line',
    icon: 'diagonal_line',
  },
  {
    id: MarkupTypes.Polygon,
    label: 'Polygon',
    icon: 'polyline',
  },
  {
    id: MarkupTypes.Rectangle,
    label: 'Rect.',
    icon: 'rectangle',
  },
  {
    id: MarkupTypes.Text,
    label: 'Text',
    icon: 'article',
  },
  {
    id: MarkupTypes.Callout,
    label: 'Call out',
    icon: 'chat_bubble',
  },
];

export type MarkupElement =
  | MarkupPointElement
  | MarkupLineElement
  | MarkupPolygonElement
  | MarkupRectangleElement
  | MarkupTextElement
  | MarkupCalloutElement;

export enum MarkupFonts {
  Arial = 'Arial',
  TimesNewRoman = 'Times New Roman',
}
export const markupFonts = Object.values(MarkupFonts);

export enum MarkupTextStyles {
  regular = 'regular',
  bold = 'bold',
  italic = 'italic',
  bolditalic = 'bolditalic',
}
export const markupTextStyles = Object.values(MarkupTextStyles);

export enum MarkupPointStyles {
  circle = 'circle',
  cross = 'cross',
  square = 'square',
  star = 'star',
}
export const markupPointStyles = Object.values(MarkupPointStyles);

export enum MarkupLineStyles {
  solid = 'solid',
  dash = 'dash',
  dot = 'dot',
  dashdot = 'dashdot',
  longdashdot = 'longdashdot',
}
export const markupLineStyles = Object.values(MarkupLineStyles);

export enum MarkupPolygonStyles {
  solid = 'solid',
  backward_diagonal = 'backward_diagonal',
  forward_diagonal = 'forward_diagonal',
  cross = 'cross',
  diagonal_cross = 'diagonal_cross',
  horizontal = 'horizontal',
  vertical = 'vertical',
}
export const markupPolygonStyles = Object.values(MarkupPolygonStyles);

interface MarkupBaseElement {
  id: string;
}

export interface MarkupPointElement extends MarkupBaseElement {
  type: MarkupTypes.Point;
  geometry: GeoJSON.Feature<GeoJSON.Point>;
  name: string;
  style: MarkupPointStyles;
  size: number;
  font: MarkupFonts;
  textStyle: MarkupTextStyles;
  textSize: number;
  color: string;
  background: string;
  fill: string;
}

export interface MarkupLineElement extends MarkupBaseElement {
  type: MarkupTypes.Line;
  geometry: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>;
  style: MarkupLineStyles;
  width: number;
  opacity: number;
  color: string;
}

export interface MarkupPolygonElement extends MarkupBaseElement {
  type: MarkupTypes.Polygon;
  geometry: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  style: MarkupPolygonStyles;
  width: number;
  interval: number;
  opacity: number;
  fill: string;
  stroke: string;
}

export interface MarkupRectangleElement
  extends Omit<MarkupPolygonElement, 'type'>,
    MarkupBaseElement {
  type: MarkupTypes.Rectangle;
}

export interface MarkupTextElement extends MarkupBaseElement {
  type: MarkupTypes.Text;
  geometry: GeoJSON.Feature<GeoJSON.Point>;
  name: string;
  font: MarkupFonts;
  size: number;
  style: MarkupTextStyles;
  background: string;
  color: string;
  backgroundOpacity: number;
}

export interface MarkupCalloutElement extends MarkupBaseElement {
  type: MarkupTypes.Callout;
  geometry: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>;
  name: string;
  size: number;
  opacity: number;
  color: string;
  background: string;
  backgroundOpacity: number;
}

/** Keys omitted from defaults - these are not style attributes */
type MarkupBaseOmitKeys = 'id' | 'type' | 'geometry';

export const MARKUP_POINT_DEFAULTS = {
  name: '',
  style: MarkupPointStyles.circle,
  size: 5,
  fill: '#0000ff',
  font: MarkupFonts.Arial,
  textSize: 10,
  color: '#ff0000',
  textStyle: MarkupTextStyles.regular,
  background: '#ffff00',
} satisfies Omit<MarkupPointElement, MarkupBaseOmitKeys>;

export const MARKUP_LINE_DEFAULTS = {
  style: MarkupLineStyles.solid,
  width: 2,
  opacity: 80,
  color: '#ff0000',
} satisfies Omit<MarkupLineElement, MarkupBaseOmitKeys>;

export const MARKUP_POLYGON_DEFAULTS = {
  style: MarkupPolygonStyles.solid,
  width: 2,
  interval: 6,
  opacity: 80,
  fill: '#0000ff',
  stroke: '#ff0000',
} satisfies Omit<MarkupPolygonElement, MarkupBaseOmitKeys>;

export const MARKUP_RECTANGLE_DEFAULTS = {
  ...MARKUP_POLYGON_DEFAULTS,
} satisfies Omit<MarkupRectangleElement, MarkupBaseOmitKeys>;

export const MARKUP_TEXT_DEFAULTS = {
  name: '',
  style: MarkupTextStyles.regular,
  size: 5,
  font: MarkupFonts.Arial,
  color: '#ff0000',
  background: '#ffff00',
  backgroundOpacity: 100,
} satisfies Omit<MarkupTextElement, MarkupBaseOmitKeys>;

export const MARKUP_CALLOUT_DEFAULTS = {
  name: '',
  size: 5,
  opacity: 80,
  color: '#ff0000',
  background: '#ffff00',
  backgroundOpacity: 100,
} satisfies Omit<MarkupCalloutElement, MarkupBaseOmitKeys>;

export type MarkupLastUsedStylesMap = {
  [MarkupTypes.Point]?: Partial<
    Omit<MarkupPointElement, MarkupBaseOmitKeys | 'name'>
  >;
  [MarkupTypes.Line]?: Partial<Omit<MarkupLineElement, MarkupBaseOmitKeys>>;
  [MarkupTypes.Polygon]?: Partial<
    Omit<MarkupPolygonElement, MarkupBaseOmitKeys>
  >;
  [MarkupTypes.Rectangle]?: Partial<
    Omit<MarkupRectangleElement, MarkupBaseOmitKeys>
  >;
  [MarkupTypes.Text]?: Partial<
    Omit<MarkupTextElement, MarkupBaseOmitKeys | 'name'>
  >;
  [MarkupTypes.Callout]?: Partial<
    Omit<MarkupCalloutElement, MarkupBaseOmitKeys | 'name'>
  >;
};

export type MarkupLayer = L.Marker | L.Polyline | L.Rectangle | L.Polygon;
